<template>
  <div
    v-if="show"
    class="lifeyrisrettindi"
    :class="{ mobile: isMobile, 'is-dark': isDark }"
  >
    <h2 class="subpage-heading m-b-50">
      {{ t('lifeyrisgreidslur_manadarleg_greidsla') }}
    </h2>

    <div v-if="isLoading" class="loading-spinner">
      <img src="../../assets/logos/Rolling-1s-200px.svg" alt="Loading..." />
    </div>

    <div v-else class="cards-row">
      <!-- Default ages from API result (filtered by userAge) -->
      <BaseCard
        v-for="calc in listForDefault"
        :key="`def-${calc.aldur}`"
        class="calc-card new-card"
      >
        <div class="age">
          {{ calc.aldur }} {{ t('lifeyrisgreidslur_ara') }}
        </div>
        <div class="amount">
          {{ formatCurrency(calc.rettindi) }}<span class="small">kr</span>
        </div>
      </BaseCard>

      <BaseCard class="calc-card new-card picker-card">
        <label class="age-input" ref="dropdownRef">
          <button
            class="custom-age-select"
            :class="{ open: isOpen }"
            @click="toggleCustomDropdown"
            type="button"
          >
            {{ t('lifeyrisgreidslur_velja_ar') }}
            <span class="age" v-if="selectedAge !== null">
              {{ selectedAge }} {{ t('lifeyrisgreidslur_ara') }}
            </span>
          </button>

          <div v-if="isOpen" class="custom-age-dropdown">
            <ul>
              <li
                v-for="age in ages"
                :key="`age-${age}`"
                @click="setSelectedAge(age)"
              >
                {{ age }} {{ t('lifeyrisgreidslur_ara') }}
              </li>
            </ul>
          </div>
        </label>

        <div :class="{ blur: reiknudrettindiAgeSpinner }">
          <div v-if="ageResult" class="amount">
            <div class="mobile-age">
              {{ ageResult.aldur }} {{ t('lifeyrisgreidslur_ara') }}
            </div>
            <div>
              {{ formatCurrency(ageResult.rettindi) }}
              <span class="small">kr</span>
            </div>
          </div>
        </div>
      </BaseCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useJoakimStore } from '@/stores/useJoakimStore'
import BaseCard from '@/components/shared/BaseCard.vue'

/** Props **/
const props = defineProps<{ show: boolean; userAge: string | number }>()

/** i18n **/
const { t } = useI18n()

/** Store **/
const joakim = useJoakimStore()
const { reiknudrettindi, reiknudrettindiAge, reiknudrettindiAgeSpinner } = storeToRefs(joakim)
const { getReiknudRettindi, getReiknudRettindiAldur, getSameign, getOnnurRettindi, ermedEingreidsluLifeyris, getGreidslusoguArs } = joakim

/** Types **/
type RettindiRow = { aldur: number; rettindi: number }

/** Local state **/
const ages = ref<number[]>([])
const selectedAge = ref<number | null>(null)
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const isLoading = ref(true)

/** Dark-mode (local, no global CSS) **/
const isDark = ref(false)
let mo: MutationObserver | null = null

/** Mobile helper **/
const isMobile = ref(false)
const handleResize = () => { isMobile.value = window.innerWidth <= 900 }

/** Derived props **/
const userAgeNum = computed(() => Number(props.userAge) || 0)

/** Default list (guarded) **/
const listForDefault = computed<RettindiRow[]>(() => {
  const src = Array.isArray(reiknudrettindi.value) ? (reiknudrettindi.value as RettindiRow[]) : []
  return src.filter(x => Number(x.aldur) >= userAgeNum.value)
})

/** Single-age result (guarded) **/
const ageResult = computed<RettindiRow | null>(() => {
  const arr = Array.isArray(reiknudrettindiAge.value) ? (reiknudrettindiAge.value as RettindiRow[]) : []
  return arr.length ? arr[0] : null
})

/** UI actions **/
function toggleCustomDropdown(event: Event) { 
  event.stopPropagation()
  isOpen.value = !isOpen.value 
}
async function setSelectedAge(age: number) {
  selectedAge.value = age
  isOpen.value = false
  await nextTick()
  await getRettindiAge()
}
async function getRettindiAge() {
  if (selectedAge.value != null) await getReiknudRettindiAldur(selectedAge.value)
}

/** Click outside to close dropdown **/
function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

/** Currency formatting **/
function formatCurrency(n?: number | string | null): string {
  if (n == null || n === '') return '0'
  const num = typeof n === 'string' ? Number(n) : n
  try { return new Intl.NumberFormat('is-IS', { maximumFractionDigits: 0 }).format(num || 0) }
  catch { return String(num ?? 0) }
}

/** Greiðslusaga year selection **/
async function getLifeyriType() {
  const targetYear = moment().month() === 0 ? moment().year() - 1 : moment().year()
  await getGreidslusoguArs(targetYear)
}

/** React to userAge prop changes **/
watch(() => props.userAge, async (val) => {
  const num = Number(val) || 60
  selectedAge.value = num
  await getRettindiAge()
})

/** Lifecycle **/
onMounted(async () => {
  isLoading.value = true
  ages.value = Array.from({ length: 21 }, (_, i) => 60 + i)
  selectedAge.value = userAgeNum.value || 60

  handleResize()
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleClickOutside)

  // local dark-mode tracker (reads body.dark-mode)
  isDark.value = document.body.classList.contains('dark-mode')
  mo = new MutationObserver(() => {
    isDark.value = document.body.classList.contains('dark-mode')
  })
  mo.observe(document.body, { attributes: true, attributeFilter: ['class'] })

  if (!Array.isArray(reiknudrettindi.value) || !reiknudrettindi.value) {
    await getReiknudRettindi()
  }

  await Promise.all([
    getRettindiAge(),
    getLifeyriType(),
    getSameign(),
    getOnnurRettindi(),
    ermedEingreidsluLifeyris()
  ])

  isLoading.value = false
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleClickOutside)
  mo?.disconnect()
})
</script>

<style scoped lang="scss">
@use '@/scss/settings/_variables.scss' as var;

/* Loading spinner */
.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  
  img {
    width: 60px;
    height: 60px;
  }
}

/* Horizontal, wrapping row of BaseCard tiles */
.cards-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
}

/* BaseCard skin for each tile */
.calc-card {
  flex: 1 1 260px;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* IMPORTANT: let dropdown escape the card */
.picker-card {
  position: relative;
  overflow: visible !important; /* overrides global .new-card overflow:hidden */
}

.blur {
  .amount { filter: blur(5px); }
  position: relative;
  &:before {
    content: "";
    width: 50px; height: 50px;
    background-image: url("../../assets//logos/Rolling-1s-200px.svg");
    background-size: cover;
    position: absolute; top: 25%; left: 25%;
    background-position: center;
  }
}

.mobile-age { display: none; }

.lifeyrisrettindi {
  width: 99%;
  margin: 20px auto 50px;
  line-height: 32px;
  font-weight: bold;

  .age { color: #319489; font-size: 24px; }
  .amount { font-size: 36px; line-height: 40px; }
  .amount .small { font-size: 24px; margin-left: 12px; }

  .age-input {
    display: flex;
    position: relative;
    z-index: 1; /* keep button above card content */
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: -4px;
    font-size: 19px;

    .custom-age-select {
      padding: 4px 12px 8px 12px;
      font-size: 16px;
      display: flex;
      justify-content: space-between;
      padding-right: 60px;
      padding-top: 6px;
      line-height: 32px;
      width: 100%;
      height: 44px;
      border: 1px solid #e6e6e6;
      border-radius: 4px;
      position: relative;
      background: #fff;
      color: var.$color-text;

      &:after {
        content: "";
        width: 20px; height: 20px;
        position: absolute; right: 12px; top: 25%;
        background-image: url("../../assets/new-icons/arrow.svg");
        background-position: center;
        border: 1px solid #e6e6e6;
      }
      &.open { &:after { transform: rotate(180deg); } }
    }

    .custom-age-dropdown {
      position: absolute;
      left: 0; right: 0; top: 43px;
      background: #fff;
      border: 1px solid #e6e6e6;
      border-radius: 6px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
      z-index: 9999;              /* sit above the card */
      max-height: 260px;
      overflow: auto;

      ul { padding: 8px 0; margin: 0; list-style: none; }
      li {
        padding: 10px 12px;
        width: 100%;
        text-align: center;
        display: flex; justify-content: center; align-items: center;
        height: 40px;
        font-weight: normal;
        color: var.$color-text;

        &:hover { cursor: pointer; background: #e6e6e620; }
      }
    }
  }
}

/* --- Dark mode: ONLY for this component --- */
.lifeyrisrettindi.is-dark {
  .age-input {
    .custom-age-select {
      background: var.$color-card-dark !important;
      color: var.$color-text-dark !important;
      border-color: var.$color-border-dark !important;

      &:after {
        /* keep same asset, just soften/invert a bit for dark */
        filter: invert(1) opacity(.8);
        border-color: var.$color-border-dark !important;
        background-color: transparent;
      }

      // &:hover {
      //   border-color: if(meta.global-variable-exists(color-border-dark-strong), var.$color-border-dark-strong, var.$color-border-dark) !important;
      // }
    }

    .custom-age-dropdown {
      background: var.$color-card-dark !important;
      color: var.$color-text-dark !important;
      border-color: var.$color-border-dark !important;
      box-shadow: 0 8px 24px rgba(0,0,0,0.35);

      li {
        color: var.$color-text-dark !important;
        &:hover { background: var.$color-table-row-dark !important; }
      }
    }
  }
}

@media (max-width: 900px) {
  .amount { display: flex; justify-content: space-between; }
  .mobile-age { display: block; font-size: 26px; color: #319489; }
  .default-calc { display: none; }
}
</style>
